import {
  AUTH_MODE_INSTANT,
  AUTH_MODE_KEY,
  INSTANT_ACCESS_SID_KEY,
  captureReauthHintFromSession,
  resolveAuthMode
} from "./reauth-hints";
import {
  clearDashboardBridgeToken,
  getDashboardBridgeToken,
  getDashboardBridgeTokenEntry,
  isDashboardBridgeConfigured,
  renewDashboardBridgeToken
} from "./dashboard-bridge";
import { createDpopProof } from "./dpop";

const AUTH0_TOKEN_STORAGE_KEY = "auth0token";
const INSTANT_ACCESS_USERNAME_KEY = "instant_access_username";
const INSTANT_ACCESS_AUTH_EVENT_THROTTLE_MS = 5000;

export const INSTANT_ACCESS_AUTH_EXPIRED_EVENT = "instant-access-auth-expired";

let lastInstantAccessAuthEventAt = 0;

// Guard to prevent concurrent getToken calls
let isGettingToken = false;
let pendingTokenPromise = null;
let pendingBridgeTokenEntryPromise = null;

function removeStorageKey(key) {
  try {
    localStorage.removeItem(key);
  } catch (_) {
    // Ignore storage access errors.
  }
}

export function isInstantAccessMode() {
  return resolveAuthMode(localStorage) === AUTH_MODE_INSTANT;
}

export function clearInstantAccessAuthState() {
  captureReauthHintFromSession(localStorage);
  removeStorageKey(AUTH0_TOKEN_STORAGE_KEY);
  removeStorageKey(AUTH_MODE_KEY);
  removeStorageKey(INSTANT_ACCESS_SID_KEY);
  removeStorageKey(INSTANT_ACCESS_USERNAME_KEY);
}

export function emitInstantAccessAuthExpired({ reason = "expired", message } = {}) {
  if (!isInstantAccessMode()) {
    return;
  }

  const now = Date.now();
  if (now - lastInstantAccessAuthEventAt < INSTANT_ACCESS_AUTH_EVENT_THROTTLE_MS) {
    clearInstantAccessAuthState();
    return;
  }

  lastInstantAccessAuthEventAt = now;
  clearInstantAccessAuthState();

  const detail = {
    reason,
    message: message || "Your Instant Access session has ended. Open your Instant Access link to sign in again."
  };

  window.dispatchEvent(new CustomEvent(INSTANT_ACCESS_AUTH_EXPIRED_EVENT, { detail }));
}

export function handleInstantAccessUnauthorized(status) {
  if (status === 401 && isInstantAccessMode()) {
    emitInstantAccessAuthExpired({
      reason: "unauthorized",
      message: "Your Instant Access session is no longer valid. Open your Instant Access link to continue."
    });
  }
}

export function init() {}

export function setEntered() {}

async function readBridgeTokenSilently() {
  const bridgeEntry = await readOrRenewBridgeTokenEntrySilently();
  return bridgeEntry?.token || null;
}

async function readOrRenewBridgeTokenEntrySilently() {
  const entry = getDashboardBridgeTokenEntry();
  if (entry) return entry;

  if (!isDashboardBridgeConfigured()) return null;

  if (!pendingBridgeTokenEntryPromise) {
    pendingBridgeTokenEntryPromise = renewBridgeTokenEntrySilently().finally(() => {
      pendingBridgeTokenEntryPromise = null;
    });
  }

  return pendingBridgeTokenEntryPromise;
}

async function renewBridgeTokenEntrySilently() {
  try {
    await renewDashboardBridgeToken();
    return getDashboardBridgeTokenEntry();
  } catch (_) {
    clearDashboardBridgeToken();
    return null;
  }
}

/**
 * Dashboard bridge tokens are kept in memory and can be renewed silently through
 * the bridge session cookie. Raw Auth0-token storage is deliberately unsupported.
 */
export async function getToken() {
  // If another getToken call is in progress, return the same promise
  if (isGettingToken && pendingTokenPromise) {
    return pendingTokenPromise;
  }

  isGettingToken = true;

  pendingTokenPromise = (async () => {
    const bridgeToken = await readBridgeTokenSilently();
    if (bridgeToken) {
      return bridgeToken;
    }

    if (isInstantAccessMode()) {
      emitInstantAccessAuthExpired({
        reason: "expired",
        message: "Your Instant Access session expired. Open your Instant Access link to continue."
      });
    }

    return null;
  })();

  try {
    return await pendingTokenPromise;
  } finally {
    // Reset the guard after the operation completes
    isGettingToken = false;
    pendingTokenPromise = null;
  }
}

export async function getAuthHeaders(url, method = "GET") {
  const bridgeEntry = await readOrRenewBridgeTokenEntrySilently();
  if (bridgeEntry?.token) {
    if (!url) {
      throw new Error("A request URL is required to create DPoP auth headers.");
    }

    return {
      Authorization: `DPoP ${bridgeEntry.token}`,
      DPoP: await createDpopProof({ url, method, accessToken: bridgeEntry.token })
    };
  }

  if (isInstantAccessMode()) {
    emitInstantAccessAuthExpired({
      reason: "expired",
      message: "Your Instant Access session expired. Open your Instant Access link to continue."
    });
  }

  return {};
}

export function clearAuthToken() {
  clearDashboardBridgeToken();
}
